import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircleIcon,
  CalendarPlusIcon,
  InfoIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  UsersRoundIcon,
  XIcon,
} from 'lucide-react';
import type { IntervisieGroup, SmoelenboekEntry, UserRole, Weekdag } from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { ApiError, apiDelete, apiGet, apiPost, apiPut } from '../api/client.js';
import { suggesties } from '../lib/naam-match.js';
import { dagnaam, inZomer, planDatums } from '../lib/intervisie-planning.js';

// Spiegel van het Weekdag-enum; de frontend bundelt geen zod (zie UUID_RE).
const WEEKDAGEN: Weekdag[] = ['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag'];
/** Meest voorkomende tijd (Jos, 2 nov 2026); per groep aan te passen. */
const STANDAARD_TIJD = '19:30 - 21:00';
const TEAMS_RE = /^https:\/\/([a-z0-9-]+\.)*teams\.microsoft\.com\//i;

/**
 * Beheer ⟶ Intervisie — suppohrt_admin maakt intervisiegroepen aan en kiest de
 * leden uit de bestaande gebruikers (in-app ledenbeheer, besluit 6 juli). De
 * Teams-link is optioneel en kan later toegevoegd worden. RBAC: suppohrt_admin
 * only, gated in de route (RoleGuard) én server-side.
 *
 * Sinds 31 juli telt daarnaast een optionele Entra security-group op bij de
 * handmatige leden (`entraGroupId`): wie die GUID in zijn `groups`-claim heeft,
 * geldt ook als lid. Geen Graph-call, dus de app kent die leden niet bij naam —
 * vandaar de expliciete uitleg en hints in dit scherm.
 */

/**
 * Mirror van `z.string().uuid()` uit `IntervisieGroupSchema` (zod v3: 8-4-4-4-12
 * hex, geen versie-/variantcontrole). Bewust hier herhaald in plaats van de
 * zod-schema's te importeren — de frontend bundelt nergens zod, en een 400 van
 * de server is een slechtere foutmelding dan een veldfout naast het invoerveld.
 * Wijkt het serverschema ooit af, dan moet deze regex mee.
 */
const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const ROLE_LABEL: Record<UserRole, string> = {
  suppohrt_admin: 'SUPPOHRT Admin',
  suppohrt_medewerker: 'Medewerker',
  zorggroep_admin: 'Zorggroep Admin',
  zorggroep_gebruiker: 'Zorggroep Gebruiker',
};

/**
 * Zoekterm en specialisme werken samen (allebei moeten matchen). Let op: dit
 * filtert alléén de keuzelijst. Wie al geselecteerd is blijft in het blok
 * daarboven staan, anders lijkt een selectie te verdwijnen bij het typen.
 */
export function filterLeden(
  users: SmoelenboekEntry[],
  search: string,
  specialisme: string,
): SmoelenboekEntry[] {
  const q = search.trim().toLowerCase();
  return users.filter((u) => {
    if (specialisme && !(u.specialisaties ?? []).includes(specialisme)) return false;
    if (!q) return true;
    return (
      u.displayName.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      // Ook op specialisatie, net als het zoekveld in Smoelenboek en Berichten:
      // die staan hieronder op het scherm, dus erop zoeken hoort te werken.
      (u.specialisaties ?? []).some((s) => s.toLowerCase().includes(q))
    );
  });
}

/**
 * E-mailadressen uit een geplakt blok: regel voor regel, komma- of
 * puntkomma-gescheiden. Lowercase en ontdubbeld, want de backend koppelt bij
 * login letterlijk op het lowercase adres uit Entra. Filtert grof op een @ in
 * het midden; het echte oordeel velt het Zod-schema op de server.
 */
export function parsePendingEmails(raw: string): string[] {
  const uniek = new Set<string>();
  for (const stuk of raw.split(/[\s,;]+/)) {
    const adres = stuk.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(adres)) continue;
    uniek.add(adres);
  }
  return [...uniek];
}

/**
 * Alle bewerkbare velden van de groep — niet alleen wat het formulier toont.
 * PUT is een VOLLEDIGE vervanging (zie `IntervisieGroupService.update`): een veld
 * dat de body niet meestuurt, wordt gewist. Een nieuw veld toevoegen aan de groep
 * zonder het hier én in `save()` op te nemen, wist het dus stilletjes bij de
 * eerstvolgende bewerking. De twee ledenlijsten zijn daarop de uitzondering:
 * weglaten laat ze ongemoeid, leeg meesturen maakt ze leeg.
 */
interface Draft {
  id: string | null; // null = nieuwe groep
  name: string;
  description: string;
  voorkeursdag: Weekdag | '';
  standaardTijd: string;
  teamsUrl: string;
  ledenUserIds: string[];
  pendingLedenEmails: string[];
  entraGroupId: string;
}

function emptyDraft(): Draft {
  return {
    id: null,
    name: '',
    description: '',
    voorkeursdag: '',
    standaardTijd: STANDAARD_TIJD,
    teamsUrl: '',
    ledenUserIds: [],
    pendingLedenEmails: [],
    entraGroupId: '',
  };
}

export function BeheerIntervisiePage(): JSX.Element {
  const [groups, setGroups] = useState<IntervisieGroup[]>([]);
  const [users, setUsers] = useState<SmoelenboekEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [plannen, setPlannen] = useState<IntervisieGroup | null>(null);
  const [melding, setMelding] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const [g, u] = await Promise.all([
        apiGet<{ groups: IntervisieGroup[] }>('/api/v1/beheer/intervisie'),
        apiGet<{ users: SmoelenboekEntry[] }>('/api/v1/users/smoelenboek'),
      ]);
      setGroups(g.groups);
      setUsers(u.users);
    } catch (err) {
      setErrorMsg(
        err instanceof ApiError
          ? `Laden mislukt (${err.status}): ${err.message}`
          : 'Kon de intervisiegroepen niet laden.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const userName = useMemo(() => {
    const map = new Map<string, SmoelenboekEntry>();
    for (const u of users) map.set(u.id, u);
    return map;
  }, [users]);

  /**
   * Wachtend adres handmatig aan een bestaande gebruiker koppelen. Eigen route
   * (geen PUT met de hele ledenlijst): Gebruikersbeheer kan tegelijk dezelfde
   * groep bewerken, en dan mag geen van beide koppelingen verloren gaan.
   */
  async function koppel(group: IntervisieGroup, adres: string, userId: string): Promise<void> {
    try {
      await apiPost(`/api/v1/beheer/intervisie/${group.id}/leden`, { userId, email: adres });
      await load();
    } catch (err) {
      setErrorMsg(
        err instanceof ApiError ? `Koppelen mislukt: ${err.message}` : 'Koppelen mislukt.',
      );
    }
  }

  async function remove(group: IntervisieGroup): Promise<void> {
    if (!window.confirm(`Groep "${group.name}" definitief verwijderen?`)) return;
    try {
      await apiDelete(`/api/v1/beheer/intervisie/${group.id}`);
      await load();
    } catch (err) {
      setErrorMsg(
        err instanceof ApiError ? `Verwijderen mislukt (${err.status}).` : 'Verwijderen mislukt.',
      );
    }
  }

  return (
    <Layout>
      <div className="mx-auto w-full max-w-4xl px-4 py-6">
        <header className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-ink-900">Beheer Intervisie</h1>
            <p className="mt-1 text-sm text-ink-900/65">
              Maak intervisiegroepen aan en kies de leden uit de bestaande gebruikers. De Teams-link
              is optioneel en kan later worden toegevoegd.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setDraft(emptyDraft())}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600"
          >
            <PlusIcon size={16} /> Nieuwe groep
          </button>
        </header>

        {melding && (
          <div className="mb-4 rounded-md bg-brand-50 px-3 py-2 text-sm text-brand-700">
            {melding}
          </div>
        )}

        {errorMsg && (
          <div className="mb-4 inline-flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircleIcon size={14} /> {errorMsg}
          </div>
        )}

        {loading ? (
          <div className="rounded-2xl border border-ring2 bg-white px-6 py-10 text-center text-sm text-ink-900/55">
            Laden…
          </div>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-ring2 bg-white px-6 py-12 text-center text-sm text-ink-900/55">
            <UsersRoundIcon size={28} className="text-ink-900/30" />
            Nog geen intervisiegroepen. Maak er een aan met “Nieuwe groep”.
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {groups.map((group) => (
              <li
                key={group.id}
                className="rounded-2xl border border-ring2 bg-white p-4 shadow-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-ink-900">{group.name}</p>
                    {group.description && (
                      <p className="mt-0.5 text-sm text-ink-900/65">{group.description}</p>
                    )}
                    {(group.voorkeursdag || group.standaardTijd) && (
                      <p className="mt-0.5 text-sm text-ink-900/80">
                        {group.voorkeursdag ? `Meestal op ${group.voorkeursdag}` : 'Geen vaste dag'}
                        {group.standaardTijd ? `, ${group.standaardTijd}` : ''}
                      </p>
                    )}
                    <p className="mt-1 text-[12px] text-ink-900/55">
                      {group.ledenUserIds.length}{' '}
                      {group.ledenUserIds.length === 1 ? 'lid' : 'leden'} handmatig gekozen
                      {(group.pendingLedenEmails ?? []).length > 0
                        ? ` · ${(group.pendingLedenEmails ?? []).length} nog niet ingelogd`
                        : ''}
                      {group.teamsUrl ? ' · Teams-link ingesteld' : ' · nog geen Teams-link'}
                      {group.entraGroupId ? ' · Entra-groep gekoppeld' : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        setMelding(null);
                        setPlannen(group);
                      }}
                      className="inline-flex items-center gap-1 rounded-lg border border-ring2 px-2.5 py-1.5 text-[13px] font-medium text-ink-900/80 hover:bg-brand-50"
                    >
                      <CalendarPlusIcon size={13} /> Bijeenkomsten plannen
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setDraft({
                          id: group.id,
                          name: group.name,
                          description: group.description ?? '',
                          voorkeursdag: group.voorkeursdag ?? '',
                          standaardTijd: group.standaardTijd ?? '',
                          teamsUrl: group.teamsUrl ?? '',
                          ledenUserIds: [...group.ledenUserIds],
                          pendingLedenEmails: [...(group.pendingLedenEmails ?? [])],
                          entraGroupId: group.entraGroupId ?? '',
                        })
                      }
                      className="inline-flex items-center gap-1 rounded-lg border border-ring2 px-2.5 py-1.5 text-[13px] font-medium text-ink-900/80 hover:bg-brand-50"
                    >
                      <PencilIcon size={13} /> Bewerken
                    </button>
                    <button
                      type="button"
                      onClick={() => void remove(group)}
                      className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-[13px] font-medium text-red-700 hover:bg-red-50"
                    >
                      <Trash2Icon size={13} /> Verwijderen
                    </button>
                  </div>
                </div>
                {(group.ledenUserIds.length > 0 || (group.pendingLedenEmails ?? []).length > 0) && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {group.ledenUserIds.map((uid) => (
                      <span
                        key={uid}
                        className="rounded-full bg-ink-900/5 px-2 py-0.5 text-[12px] text-ink-900/70"
                      >
                        {userName.get(uid)?.displayName ?? uid}
                      </span>
                    ))}
                    {(group.pendingLedenEmails ?? []).map((adres) => (
                      <span
                        key={adres}
                        className="inline-flex items-center gap-1.5 rounded-full bg-ring1-bg py-0.5 pl-2 pr-1 text-[12px] text-ring1-fg ring-1 ring-ring1-border"
                      >
                        {adres}
                        <KoppelSelect
                          adres={adres}
                          users={users}
                          onKies={(userId) => void koppel(group, adres, userId)}
                        />
                      </span>
                    ))}
                  </div>
                )}
                {group.entraGroupId && (
                  <p className="mt-2 flex items-start gap-1.5 rounded-md border border-mosterdgeel-100 bg-mosterdgeel-10 px-2.5 py-1.5 text-[12px] leading-relaxed text-ink-900/75">
                    <InfoIcon size={13} className="mt-0.5 shrink-0 text-mosterdgeel-600" />
                    <span>
                      Leden uit de gekoppelde Entra-groep tellen ook mee, maar staan niet in de
                      ledenlijst hierboven: zonder Microsoft Graph kan de app die namen niet
                      ophalen.
                    </span>
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {plannen && (
        <PlanBijeenkomsten
          group={plannen}
          onClose={() => setPlannen(null)}
          onSaved={(aantal) => {
            setPlannen(null);
            setMelding(
              `${aantal} ${aantal === 1 ? 'bijeenkomst' : 'bijeenkomsten'} van ${plannen.name} staan in de agenda. Losse datums wijzig je daar.`,
            );
          }}
        />
      )}

      {draft && (
        <GroupEditor
          draft={draft}
          users={users}
          onClose={() => setDraft(null)}
          onSaved={() => {
            setDraft(null);
            void load();
          }}
        />
      )}
    </Layout>
  );
}

/**
 * Keuzelijst om een wachtend adres aan een bestaande gebruiker te hangen.
 * Bovenaan wat op de naam lijkt (patroon van de TimeChimp-koppeling): de reden
 * dat dit adres niet vanzelf koppelde is nou juist dat het afwijkt van het
 * Entra-adres, dus alfabetisch zoeken helpt niet.
 */
function KoppelSelect({
  adres,
  users,
  onKies,
}: {
  adres: string;
  users: SmoelenboekEntry[];
  onKies: (userId: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const { voorgesteld, overig } = suggesties(
    { displayName: adres.split('@')[0] ?? '', email: adres },
    users,
    4,
  );
  const optie = (u: SmoelenboekEntry) => (
    <option key={u.id} value={u.id}>
      {u.displayName} · {u.email}
    </option>
  );
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Dit adres aan een bestaande gebruiker koppelen"
        className="text-ring1-fg/70 hover:underline"
      >
        koppel
      </button>
    );
  }
  return (
    <select
      autoFocus
      value=""
      aria-label={`Koppel ${adres} aan een gebruiker`}
      onBlur={() => setOpen(false)}
      onChange={(e) => {
        setOpen(false);
        if (e.target.value) onKies(e.target.value);
      }}
      className="max-w-[11rem] rounded-md border border-ring1-border bg-white px-1 py-0.5 text-[12px] text-ink-900"
    >
      <option value="">Kies een gebruiker…</option>
      {voorgesteld.length > 0 && (
        <optgroup label="Waarschijnlijk deze">{voorgesteld.map(optie)}</optgroup>
      )}
      <optgroup label="Alle gebruikers">{overig.map(optie)}</optgroup>
    </select>
  );
}

function GroupEditor({
  draft,
  users,
  onClose,
  onSaved,
}: {
  draft: Draft;
  users: SmoelenboekEntry[];
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [name, setName] = useState(draft.name);
  const [description, setDescription] = useState(draft.description);
  const [voorkeursdag, setVoorkeursdag] = useState<Weekdag | ''>(draft.voorkeursdag);
  const [standaardTijd, setStandaardTijd] = useState(draft.standaardTijd);
  const [teamsUrl, setTeamsUrl] = useState(draft.teamsUrl);
  const [ledenUserIds, setLedenUserIds] = useState<string[]>(draft.ledenUserIds);
  const [pendingLedenEmails, setPendingLedenEmails] = useState<string[]>(draft.pendingLedenEmails);
  const [emailInvoer, setEmailInvoer] = useState('');
  const [entraGroupId, setEntraGroupId] = useState(draft.entraGroupId);
  const [search, setSearch] = useState('');
  const [specialisme, setSpecialisme] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const entraTrimmed = entraGroupId.trim();
  const entraInvalid = entraTrimmed !== '' && !UUID_RE.test(entraTrimmed);
  // Waarschuwen, niet blokkeren: de backend accepteert beide vormen en een
  // toekomstige linkvariant mag niet stuklopen op deze heuristiek. Naast de
  // deeplinks ook de URL uit de adresbalk van een geopend kanaal, want dat is
  // wat een beheerder in de praktijk kopieert.
  const lijktTeamLink =
    /\/l\/(team|channel|chat|entity)\//i.test(teamsUrl) ||
    /ctx=channel|\/conversations\//i.test(teamsUrl);

  const selected = useMemo(() => new Set(ledenUserIds), [ledenUserIds]);

  /** Alle specialismen die in het smoelenboek voorkomen, alfabetisch (NL). */
  const specialismen = useMemo(() => {
    const set = new Set<string>();
    for (const u of users) for (const s of u.specialisaties ?? []) set.add(s);
    return [...set].sort((a, b) => a.localeCompare(b, 'nl'));
  }, [users]);

  const filtered = useMemo(
    () => filterLeden(users, search, specialisme),
    [users, search, specialisme],
  );

  /**
   * Geselecteerde leden staan apart bovenaan, los van zoek- en filterresultaat.
   * Zonder dat blok lijkt een selectie te verdwijnen zodra je een nieuwe naam
   * typt: de eerder gekozen personen vallen dan simpelweg buiten het filter.
   *
   * Mapt over `ledenUserIds` en niet over `users`, zodat een lid dat niet meer
   * in het smoelenboek staat (uit dienst, andere rol) zichtbaar en verwijderbaar
   * blijft in plaats van stil te verdwijnen achter een te hoge teller.
   */
  const gekozen = useMemo(
    () =>
      ledenUserIds
        .map((id) => ({ id, user: users.find((u) => u.id === id) }))
        .sort((a, b) =>
          (a.user?.displayName ?? a.id).localeCompare(b.user?.displayName ?? b.id, 'nl'),
        ),
    [ledenUserIds, users],
  );

  function voegEmailsToe(): void {
    const nieuw = parsePendingEmails(emailInvoer);
    if (nieuw.length === 0) {
      setErr('Geen geldig e-mailadres herkend.');
      return;
    }
    setErr(null);
    setPendingLedenEmails((cur) => [...new Set([...cur, ...nieuw])]);
    setEmailInvoer('');
  }

  function toggle(id: string): void {
    setLedenUserIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  async function save(): Promise<void> {
    if (!name.trim()) {
      setErr('Naam is verplicht.');
      return;
    }
    if (entraInvalid) {
      setErr('Controleer het object-ID van de Entra-groep.');
      return;
    }
    setBusy(true);
    setErr(null);
    // PUT vervangt het hele document: elk bewerkbaar veld moet hier mee, ook als
    // het formulier het niet aanpaste. Een weggelaten veld = gewist veld, dus een
    // ontbrekende `entraGroupId`/`teamsUrl` sloopt stil de bestaande koppeling.
    const body = {
      name: name.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(voorkeursdag ? { voorkeursdag } : {}),
      ...(standaardTijd.trim() ? { standaardTijd: standaardTijd.trim() } : {}),
      ...(teamsUrl.trim() ? { teamsUrl: teamsUrl.trim() } : {}),
      // Beide ledenlijsten gaan ALTIJD mee, ook leeg: de server ziet weglaten als
      // "ongewijzigd", dus zonder deze regels is "alle leden verwijderen" niet uit
      // te voeren vanuit dit scherm.
      ledenUserIds,
      // Wat nog in het invoerveld staat telt mee, want een beheerder die een lijst
      // plakt en meteen op Opslaan drukt verwacht niet dat hij eerst nog op
      // "Toevoegen aan de groep" had moeten klikken.
      pendingLedenEmails: [...new Set([...pendingLedenEmails, ...parsePendingEmails(emailInvoer)])],
      ...(entraTrimmed ? { entraGroupId: entraTrimmed } : {}),
    };
    try {
      if (draft.id) {
        await apiPut(`/api/v1/beheer/intervisie/${draft.id}`, body);
      } else {
        await apiPost('/api/v1/beheer/intervisie', body);
      }
      onSaved();
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? e.status === 400
            ? 'Controleer de invoer — een Teams-link moet een https teams.microsoft.com-URL zijn en een Entra-object-ID een geldige GUID.'
            : `Opslaan mislukt (${e.status}).`
          : 'Opslaan mislukt.',
      );
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-lg">
        <div className="flex items-center justify-between border-b border-brand-100 px-5 py-3">
          <h2 className="text-[15px] font-bold text-ink-900">
            {draft.id ? 'Groep bewerken' : 'Nieuwe intervisiegroep'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluiten"
            className="rounded-md p-1 text-ink-900/60 hover:bg-brand-50 hover:text-ink-900"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <label className="mb-1 block text-[13px] font-medium text-ink-900/80">Naam</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            placeholder="Bijv. Intervisiegroep Volwassenen"
            className="mb-3 w-full rounded-md border border-ring2 px-3 py-1.5 text-sm"
          />

          <label className="mb-1 block text-[13px] font-medium text-ink-900/80">
            Omschrijving <span className="font-normal text-ink-900/45">(optioneel)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={2}
            className="mb-3 w-full rounded-md border border-ring2 px-3 py-1.5 text-sm"
          />

          <div className="mb-3 grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="voorkeursdag"
                className="mb-1 block text-[13px] font-medium text-ink-900/80"
              >
                Voorkeursdag
              </label>
              <select
                id="voorkeursdag"
                value={voorkeursdag}
                onChange={(e) => setVoorkeursdag(e.target.value as Weekdag | '')}
                className="w-full rounded-md border border-ring2 px-2 py-1.5 text-sm text-ink-900"
              >
                <option value="">Geen vaste dag</option>
                {WEEKDAGEN.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="standaard-tijd"
                className="mb-1 block text-[13px] font-medium text-ink-900/80"
              >
                Standaardtijd
              </label>
              <input
                id="standaard-tijd"
                type="text"
                value={standaardTijd}
                onChange={(e) => setStandaardTijd(e.target.value)}
                maxLength={50}
                placeholder={STANDAARD_TIJD}
                className="w-full rounded-md border border-ring2 px-3 py-1.5 text-sm"
              />
            </div>
          </div>

          <label className="mb-1 block text-[13px] font-medium text-ink-900/80">
            Teams-link van de groep <span className="font-normal text-ink-900/45">(optioneel)</span>
          </label>
          <input
            type="url"
            value={teamsUrl}
            onChange={(e) => setTeamsUrl(e.target.value)}
            placeholder="https://teams.microsoft.com/l/meetup-join/…"
            className="mb-1 w-full rounded-md border border-ring2 px-3 py-1.5 text-sm"
          />
          {/* Een teamlink opent het kanaal, geen gesprek: dan klikt een lid zich
              op het sessiemoment naar een tegel in plaats van naar de vergadering. */}
          {lijktTeamLink ? (
            <p className="mb-4 text-[12px] text-error">
              Dit lijkt een link naar een Teams-team, niet naar een vergadering. Gebruik in Teams
              &quot;Vergaderingsopties&quot; of &quot;Deelnamegegevens kopiëren&quot;; die link
              bevat <code>meetup-join</code>.
            </p>
          ) : (
            <p className="mb-4 text-[12px] text-ink-900/70">
              Geldt voor bijeenkomsten zonder eigen link. Heeft elke datum een eigen vergadering,
              vul die dan per datum in bij &quot;Bijeenkomsten plannen&quot;.
            </p>
          )}

          <div className="mb-1 flex items-center justify-between">
            <label className="text-[13px] font-medium text-ink-900/80">Leden</label>
            <span className="text-[12px] text-ink-900/55">{ledenUserIds.length} geselecteerd</span>
          </div>
          {gekozen.length > 0 && (
            <ul
              aria-label="Geselecteerde leden"
              className="mb-2 flex flex-wrap gap-1.5 rounded-md border border-brand-100 bg-brand-50/40 p-2"
            >
              {gekozen.map(({ id, user }) => (
                <li
                  key={id}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[12px] font-medium ring-1 ${
                    user
                      ? 'bg-white text-ink-900 ring-brand-100'
                      : 'bg-ring1-bg text-ring1-fg ring-ring1-border'
                  }`}
                >
                  {/* Alleen het kruisje verwijdert, zoals bij de specialisatie-chips
                      in Profile: op een naam klikken mag niemand uit de groep gooien. */}
                  {user?.displayName ?? id}
                  <button
                    type="button"
                    onClick={() => toggle(id)}
                    aria-label={`Verwijder ${user?.displayName ?? id} uit deze groep`}
                    className="rounded-full p-0.5 text-ink-900/60 hover:bg-error/10 hover:text-error"
                  >
                    <XIcon size={12} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {gekozen.some((g) => !g.user) && (
            <p className="mb-2 text-[12px] text-ink-900/70">
              Geel gemarkeerde leden staan niet meer in het smoelenboek, bijvoorbeeld omdat zij uit
              dienst zijn. Zij tellen nog wel mee; verwijder ze met het kruisje.
            </p>
          )}

          <div className="mb-2 flex gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Zoek op naam of e-mail…"
              className="min-w-0 flex-1 rounded-md border border-ring2 px-3 py-1.5 text-sm"
            />
            {specialismen.length > 0 && (
              <select
                value={specialisme}
                onChange={(e) => setSpecialisme(e.target.value)}
                aria-label="Filter op specialisme"
                className="shrink-0 rounded-md border border-ring2 px-2 py-1.5 text-sm text-ink-900"
              >
                <option value="">Alle specialismen</option>
                {specialismen.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="max-h-60 overflow-y-auto rounded-md border border-ring2">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-[13px] text-ink-900/50">
                Geen gebruikers gevonden.
              </p>
            ) : (
              filtered.map((u) => (
                <label
                  key={u.id}
                  className="flex cursor-pointer items-center gap-2.5 border-b border-brand-50 px-3 py-2 text-sm last:border-b-0 hover:bg-brand-50/50"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(u.id)}
                    onChange={() => toggle(u.id)}
                    className="h-4 w-4 accent-brand-500"
                  />
                  <span className="min-w-0 flex-1 truncate text-ink-900">
                    {u.displayName}
                    {u.specialisaties && u.specialisaties.length > 0 && (
                      <span className="block truncate text-[11px] text-ink-900/70">
                        {u.specialisaties.join(' · ')}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-[11px] text-ink-900/70">{ROLE_LABEL[u.role]}</span>
                </label>
              ))
            )}
          </div>

          <label
            htmlFor="pending-leden-emails"
            className="mb-1 mt-4 block text-[13px] font-medium text-ink-900/80"
          >
            Leden op e-mailadres{' '}
            <span className="font-normal text-ink-900/45">(nog niet ingelogd)</span>
          </label>
          <p className="mb-1.5 text-[12px] leading-relaxed text-ink-900/70">
            Staat iemand niet in de lijst hierboven, dan heeft die nog nooit ingelogd. Plak hier het
            e-mailadres; zodra die persoon voor het eerst inlogt, wordt hij automatisch lid van deze
            groep. Eén adres per regel of gescheiden door komma&apos;s.
          </p>
          {pendingLedenEmails.length > 0 && (
            <ul
              aria-label="Leden op e-mailadres"
              className="mb-2 flex flex-wrap gap-1.5 rounded-md border border-mosterdgeel-100 bg-mosterdgeel-10 p-2"
            >
              {pendingLedenEmails.map((adres) => (
                <li
                  key={adres}
                  className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-0.5 text-[12px] font-medium text-ink-900 ring-1 ring-ring1-border"
                >
                  {adres}
                  <span className="text-ink-900/55">nog niet ingelogd</span>
                  <button
                    type="button"
                    onClick={() => setPendingLedenEmails((cur) => cur.filter((x) => x !== adres))}
                    aria-label={`Verwijder ${adres} uit deze groep`}
                    className="rounded-full p-0.5 text-ink-900/60 hover:bg-error/10 hover:text-error"
                  >
                    <XIcon size={12} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <textarea
            id="pending-leden-emails"
            value={emailInvoer}
            onChange={(e) => setEmailInvoer(e.target.value)}
            rows={3}
            spellCheck={false}
            placeholder={'naam@suppohrt.nl\nandere.naam@suppohrt.nl'}
            className="w-full rounded-md border border-ring2 px-3 py-1.5 text-sm"
          />
          <button
            type="button"
            onClick={voegEmailsToe}
            className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg border border-ring2 px-2.5 py-1.5 text-[13px] font-medium text-ink-900/80 hover:bg-brand-50"
          >
            <PlusIcon size={13} /> Toevoegen aan de groep
          </button>

          <label
            htmlFor="entra-group-id"
            className="mb-1 mt-4 block text-[13px] font-medium text-ink-900/80"
          >
            Entra-groep (object-ID) <span className="font-normal text-ink-900/45">(optioneel)</span>
          </label>
          <input
            id="entra-group-id"
            type="text"
            value={entraGroupId}
            onChange={(e) => setEntraGroupId(e.target.value)}
            maxLength={36}
            spellCheck={false}
            autoComplete="off"
            placeholder="00000000-0000-0000-0000-000000000000"
            aria-invalid={entraInvalid}
            aria-describedby="entra-group-id-uitleg"
            className={`w-full rounded-md border px-3 py-1.5 font-mono text-sm ${
              entraInvalid ? 'border-error bg-error/5' : 'border-ring2'
            }`}
          />
          {entraInvalid && (
            <p className="mt-1 flex items-start gap-1.5 text-[12px] text-error">
              <AlertCircleIcon size={13} className="mt-0.5 shrink-0" />
              <span>
                Dit is geen geldig object-ID. Verwacht 32 hexadecimale tekens met streepjes,
                bijvoorbeeld 50f4f524-2272-4f57-9f41-0285dfc2820d.
              </span>
            </p>
          )}
          <p
            id="entra-group-id-uitleg"
            className="mt-1.5 text-[12px] leading-relaxed text-ink-900/60"
          >
            Leden van deze Entra-groep tellen automatisch mee, náást de leden die je hierboven
            handmatig kiest. Let op: alleen Entra{' '}
            <strong className="font-semibold">security groups</strong> werken — Microsoft 365- en
            Teams-groepen niet; het object-ID krijg je handmatig van SUPPOHRT IT en de app kan het
            niet controleren; en een lidmaatschap werkt pas door nadat het lid opnieuw heeft
            ingelogd.
          </p>
          {entraTrimmed !== '' && !entraInvalid && (
            <p className="mt-2 flex items-start gap-1.5 rounded-md border border-mosterdgeel-100 bg-mosterdgeel-10 px-2.5 py-1.5 text-[12px] leading-relaxed text-ink-900/75">
              <InfoIcon size={13} className="mt-0.5 shrink-0 text-mosterdgeel-600" />
              <span>
                De leden uit deze Entra-groep verschijnen niet in de ledenlijst hierboven. Zonder
                Microsoft Graph kan de app niet opvragen wie erin zitten.
              </span>
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-brand-100 px-5 py-3">
          {err && <span className="mr-auto text-[12px] text-red-700">{err}</span>}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-ink-900/70 hover:bg-brand-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            disabled={busy || entraInvalid}
            onClick={() => void save()}
            className="rounded-lg bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
          >
            Opslaan
          </button>
        </div>
      </div>
    </div>
  );
}

interface PlanRij {
  key: number;
  date: string;
  time: string;
  teamsUrl: string;
}

function vandaag(): string {
  // sv-SE geeft YYYY-MM-DD; de tijdzone houdt "vandaag" op Nederlandse tijd.
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Amsterdam' });
}

function plusMaanden(datum: string, n: number): string {
  const d = new Date(`${datum}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * Een reeks bijeenkomsten in één keer in de agenda zetten (besluit Jos 2 nov
 * 2026). Het formulier rekent een voorstel uit (voorkeursdag, ritme, pauze);
 * daarna is elke regel los aan te passen of te schrappen. Opslaan maakt van
 * elke regel een gewoon agenda-item, dus wijzigen kan later ook in de agenda.
 * Per datum een eigen Teams-link, want de opleidingen-medewerker zet per
 * halfjaar losse vergaderingen klaar (overleg 2 nov, r.224).
 */
function PlanBijeenkomsten({
  group,
  onClose,
  onSaved,
}: {
  group: IntervisieGroup;
  onClose: () => void;
  onSaved: (aantal: number) => void;
}): JSX.Element {
  const [start, setStart] = useState(vandaag());
  const [eind, setEind] = useState(plusMaanden(vandaag(), 6));
  const [intervalWeken, setIntervalWeken] = useState(6);
  const [metPauze, setMetPauze] = useState(false);
  const [pauzeVan, setPauzeVan] = useState('');
  const [pauzeTot, setPauzeTot] = useState('');
  const [rijen, setRijen] = useState<PlanRij[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const tijd = group.standaardTijd || STANDAARD_TIJD;

  function voorstel(): void {
    const datums = planDatums({
      start,
      eind,
      intervalWeken,
      voorkeursdag: group.voorkeursdag,
      ...(metPauze && pauzeVan && pauzeTot ? { pauze: { van: pauzeVan, tot: pauzeTot } } : {}),
    });
    setRijen(datums.map((date, i) => ({ key: i, date, time: tijd, teamsUrl: '' })));
    setErr(datums.length === 0 ? 'Geen datums in deze periode.' : null);
  }

  // Eerste voorstel direct tonen; daarna alleen op de knop, zodat een
  // gewijzigde instelling geen al ingevulde links overschrijft.
  useEffect(voorstel, []);

  function wijzig(key: number, veld: 'date' | 'time' | 'teamsUrl', waarde: string): void {
    setRijen((cur) => cur.map((r) => (r.key === key ? { ...r, [veld]: waarde } : r)));
  }

  async function opslaan(): Promise<void> {
    const geldig = rijen.filter((r) => r.date);
    if (geldig.length === 0) {
      setErr('Er staan geen datums in de lijst.');
      return;
    }
    if (geldig.some((r) => !r.time.trim())) {
      setErr('Elke bijeenkomst heeft een tijd nodig.');
      return;
    }
    if (geldig.some((r) => r.teamsUrl.trim() && !TEAMS_RE.test(r.teamsUrl.trim()))) {
      setErr(
        'Een Teams-link klopt niet. Plak de deelnamelink; die begint met https://teams.microsoft.com/.',
      );
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const res = await apiPost<{ aantal: number }>(
        `/api/v1/beheer/intervisie/${group.id}/bijeenkomsten`,
        {
          bijeenkomsten: geldig.map((r) => ({
            date: r.date,
            time: r.time.trim(),
            teamsUrl: r.teamsUrl.trim(),
          })),
        },
      );
      onSaved(res.aantal);
    } catch (e) {
      setErr(
        e instanceof ApiError ? `Opslaan mislukt (${e.status}): ${e.message}` : 'Opslaan mislukt.',
      );
      setBusy(false);
    }
  }

  const veld = 'rounded-md border border-ring2 px-2 py-1.5 text-sm';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-lg">
        <div className="flex items-center justify-between border-b border-brand-100 px-5 py-3">
          <h2 className="text-[15px] font-bold text-ink-900">
            Bijeenkomsten plannen: {group.name}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluiten"
            className="rounded-md p-1 text-ink-900/60 hover:bg-brand-50 hover:text-ink-900"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <p className="mb-3 text-[13px] text-ink-900/70">
            {group.voorkeursdag
              ? `De datums vallen op de voorkeursdag (${group.voorkeursdag}). `
              : 'Deze groep heeft geen voorkeursdag; stel die in bij Bewerken of pas de datums hieronder aan. '}
            Je kunt elke datum daarna nog verschuiven of weghalen.
          </p>
          <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <label className="text-[13px] font-medium text-ink-900/80">
              Vanaf
              <input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className={`mt-1 w-full ${veld}`}
              />
            </label>
            <label className="text-[13px] font-medium text-ink-900/80">
              Tot en met
              <input
                type="date"
                value={eind}
                onChange={(e) => setEind(e.target.value)}
                className={`mt-1 w-full ${veld}`}
              />
            </label>
            <label className="text-[13px] font-medium text-ink-900/80">
              Elke … weken
              <input
                type="number"
                min={1}
                max={26}
                value={intervalWeken}
                onChange={(e) => setIntervalWeken(Number(e.target.value))}
                className={`mt-1 w-full ${veld}`}
              />
            </label>
          </div>
          <label className="mb-2 flex items-center gap-2 text-[13px] text-ink-900/80">
            <input
              type="checkbox"
              checked={metPauze}
              onChange={(e) => setMetPauze(e.target.checked)}
              className="h-4 w-4 accent-brand-500"
            />
            Pauze inlassen (bijvoorbeeld de zomervakantie); daarna gaat het ritme opnieuw in
          </label>
          {metPauze && (
            <div className="mb-3 grid grid-cols-2 gap-3">
              <label className="text-[13px] font-medium text-ink-900/80">
                Pauze van
                <input
                  type="date"
                  value={pauzeVan}
                  onChange={(e) => setPauzeVan(e.target.value)}
                  className={`mt-1 w-full ${veld}`}
                />
              </label>
              <label className="text-[13px] font-medium text-ink-900/80">
                Pauze tot en met
                <input
                  type="date"
                  value={pauzeTot}
                  onChange={(e) => setPauzeTot(e.target.value)}
                  className={`mt-1 w-full ${veld}`}
                />
              </label>
            </div>
          )}
          <button
            type="button"
            onClick={voorstel}
            className="mb-4 inline-flex items-center gap-1.5 rounded-lg border border-ring2 px-2.5 py-1.5 text-[13px] font-medium text-ink-900/80 hover:bg-brand-50"
          >
            Datums opnieuw berekenen
          </button>

          <ul className="flex flex-col gap-2" aria-label="Te plannen bijeenkomsten">
            {rijen.map((r) => (
              <li key={r.key} className="rounded-lg border border-ring2 p-2">
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="date"
                    value={r.date}
                    onChange={(e) => wijzig(r.key, 'date', e.target.value)}
                    aria-label="Datum"
                    className={veld}
                  />
                  <span className="w-20 text-[12px] text-ink-900/70">
                    {r.date ? dagnaam(r.date) : ''}
                  </span>
                  <input
                    type="text"
                    value={r.time}
                    onChange={(e) => wijzig(r.key, 'time', e.target.value)}
                    aria-label="Tijd"
                    maxLength={50}
                    className={`w-32 ${veld}`}
                  />
                  <button
                    type="button"
                    onClick={() => setRijen((cur) => cur.filter((x) => x.key !== r.key))}
                    aria-label={`Bijeenkomst ${r.date} weghalen`}
                    className="ml-auto rounded-md p-1 text-ink-900/60 hover:bg-error/10 hover:text-error"
                  >
                    <Trash2Icon size={15} />
                  </button>
                </div>
                <input
                  type="url"
                  value={r.teamsUrl}
                  onChange={(e) => wijzig(r.key, 'teamsUrl', e.target.value)}
                  aria-label={`Teams-link ${r.date}`}
                  placeholder={
                    group.teamsUrl
                      ? 'Teams-link (leeg = link van de groep)'
                      : 'Teams-link (optioneel, kan later in de agenda)'
                  }
                  className={`mt-2 w-full ${veld}`}
                />
                {r.date && inZomer(r.date) && (
                  <p className="mt-1 text-[12px] text-ring1-fg">
                    Deze datum valt mogelijk in de zomervakantie.
                  </p>
                )}
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() =>
              setRijen((cur) => [
                ...cur,
                {
                  key: Math.max(-1, ...cur.map((x) => x.key)) + 1,
                  date: '',
                  time: tijd,
                  teamsUrl: '',
                },
              ])
            }
            className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-ring2 px-2.5 py-1.5 text-[13px] font-medium text-ink-900/80 hover:bg-brand-50"
          >
            <PlusIcon size={13} /> Datum toevoegen
          </button>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-brand-100 px-5 py-3">
          {err && <span className="mr-auto text-[12px] text-red-700">{err}</span>}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-ink-900/70 hover:bg-brand-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            disabled={busy || rijen.length === 0}
            onClick={() => void opslaan()}
            className="rounded-lg bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {rijen.length} {rijen.length === 1 ? 'bijeenkomst' : 'bijeenkomsten'} in de agenda
            zetten
          </button>
        </div>
      </div>
    </div>
  );
}
